import { NavLink } from 'react-router-dom';
import {
  IconPhoto,
  IconUpload,
  IconCalendarTime,
  IconMap2,
  IconShieldLock,
  IconLogout,
} from '@tabler/icons-react';
import { ThemeToggle } from './ThemeToggle.tsx';
import { ShortcutsDialog } from './ShortcutsDialog.tsx';
import { useAuthStore } from '../../state/authStore.ts';
import { cn } from '../../lib/cn.ts';

const LINKS = [
  { to: '/events', label: 'Events', icon: IconPhoto },
  { to: '/timeline', label: 'Timeline', icon: IconCalendarTime },
  { to: '/map', label: 'Map', icon: IconMap2 },
  { to: '/upload', label: 'Upload', icon: IconUpload },
];

export function NavBar() {
  const me = useAuthStore((s) => s.me);
  const signOut = useAuthStore((s) => s.signOut);
  const isAdmin = me?.role === 'admin';

  return (
    <header className="sticky top-0 z-40 px-4 pt-4">
      <div className="mx-auto max-w-6xl">
        {/* Wordmark — standalone header, no box, breaks out of the chrome */}
        <div className="mb-3 flex items-center justify-between px-1">
          <NavLink to="/events" className="group flex items-center gap-2.5">
            {/* Logo sits on a white chip so the dark grayscale mark stays crisp
                on both themes. */}
            <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-line transition-transform duration-200 ease-standard group-hover:scale-105">
              <img src="/logo.png" alt="" className="h-full w-full object-contain p-0.5" />
            </span>
            <span className="flex flex-col">
              <span className="font-display text-[1.75rem] leading-none tracking-tight">
                <span className="font-bold text-fg">Bhandari</span>{' '}
                <span className="font-normal text-fg-2">Photos</span>
              </span>
              <span className="mt-1 text-[0.625rem] font-medium uppercase tracking-wider text-muted">
                Memories • Moments • Forever
              </span>
            </span>
          </NavLink>

          <div className="flex items-center gap-1">
            {me && (
              <span className="mr-1 hidden text-sm text-fg-2 sm:inline" title={me.email}>
                {me.name || me.email}
              </span>
            )}
            <ShortcutsDialog />
            <ThemeToggle />
            {me && (
              <button
                onClick={signOut}
                className="grid h-9 w-9 place-items-center rounded-xl border border-line bg-surface text-fg-2 shadow-sm transition duration-200 ease-standard hover:bg-grid"
                aria-label="Sign out"
                title="Sign out"
              >
                <IconLogout className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation tabs — their own pill, separate from the brand */}
        <nav className="glass-chrome border border-line flex items-center gap-1 rounded-2xl px-2 py-1.5">
          {LINKS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium duration-200 ease-standard',
                  isActive ? 'bg-accent text-white shadow-sm' : 'text-fg-2 hover:bg-grid',
                )
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'ml-auto inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium duration-200 ease-standard',
                  isActive ? 'bg-accent text-white shadow-sm' : 'text-fg-2 hover:bg-grid',
                )
              }
            >
              <IconShieldLock className="h-4 w-4" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
      </div>
    </header>
  );
}
